import {useEffect} from 'react';
import {Redirect, Route, Switch, useParams, useRouteMatch} from 'react-router-dom';
import {useAppContext} from '../../../../../../contexts/app/context';
import {useOffice} from '../../../../../../contexts/office/context';
import SeniorApplicationsProfessionalCaregiverOfficeScreen from './applications/senior-applications.professional-caregiver.screen';
import SeniorAssociateDeviceProfessionalCaregiverOfficeScreen from './associate-device/senior-associate-device.professional-caregiver.screen';
import SeniorAssociateUserCardProfessionalCaregiverOfficeScreen from './associate-user-card/senior-associate-user-card.professional-caregiver.screen';
import SeniorDevicesProfessionalCaregiverOfficeScreen from './devices/senior-devices.professional-caregiver.screen';
import SeniorHealthProfessionalCaregiverOfficeScreen from './health/senior-health.professional-caregiver.screen';
import SeniorInformalCaregiversProfessionalCaregiverOfficeScreen from './informal-caregivers/senior-informal-caregivers.professional-caregiver.screen';
import SeniorInviteInformalCaregiverProfessionalCaregiverOfficeScreen from './invite-informal-caregiver/senior-invite-informal-caregiver.professional-caregiver.screen';
import SeniorInvitesHistoryProfessionalCaregiverOfficeScreen from './invites-history/senior-invites-history.professional-caregiver.screen';
import SeniorPlaylistItemsProfessionalCaregiverOfficeScreen from './playlists/senior-playlist-items.professional-caregiver.screen';
import SeniorPlaylistsProfessionalCaregiverOfficeScreen from './playlists/senior-playlists.professional-caregiver.screen';
import SeniorProfileProfessionalCaregiverOfficeScreen from './profile/senior-profile.professional-caregiver.screen';
import SeniorUserCardsProfessionalCaregiverOfficeScreen from './user-cards/senior-user-cards.professional-caregiver.screen';
import SeniorUserCardProfessionalCaregiverOfficeScreen from './user-cards/senior-user-card.professional-caregiver.screen';
import SeniorPlaylistItemProfessionalCaregiverOfficeScreen from './playlists/senior-playlist-item.professional-caregiver.screen';
import SeniorDeviceDetailsProfessionalCaregiverOfficeScreen from './devices/senior-device-details.professional-caregiver.screen';
import {UsersRepository} from '../../../../../../repositories/users';
import {User} from '../../../../../../backend/interfaces/user';
import {UserType} from '../../../../../../backend/enums/user-type';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {ScreenPaths} from '../../../../../../enums';
import PermissionsRequiredRoute from '../../../../../../components/permissions-required-route';
import AccessRequiredRoute from '../../../../../../components/access-required-route';
import SeniorAttachmentsProfessionalCaregiverOfficeScreen from './attachments/senior-attachments.professional-caregiver.screen';

interface SeniorPersonalDataProfessionalCaregiverOfficeScreenProps {
  defaultRoute?: ScreenPaths;
}

export default function SeniorPersonalDataProfessionalCaregiverOfficeScreen({
  defaultRoute = ScreenPaths.Senior_Profile,
}: SeniorPersonalDataProfessionalCaregiverOfficeScreenProps) {
  let {id, entityId}: {id: string; entityId: string} = useParams();

  let {path, url} = useRouteMatch();

  const appContext = useAppContext();

  const officeContext = useOffice();

  // Validate and get the user
  useEffect(() => {
    const getUser = async () => {
      if (id) {
        try {
          // Get user
          const userDetails: User = await UsersRepository.instance.getById(id);

          if (userDetails) {
            // Set user for edit/view
            officeContext.setUserForm(userDetails);
          } else {
            // Error
            throw new Error('Empty user.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get user.', error);
        }
      } else {
        // Set user for create
        officeContext.setUserForm({
          id: undefined,
          product: appContext.state.configuration.product,
          type: UserType.Senior,
          name: '',
          isActive: true,
          email: undefined,
          picture: undefined,
          birthday: undefined,
          phoneNumber: undefined,
          taxPayerNumber: undefined,
          gender: undefined,
          address: undefined,
          languageCode: undefined,
          religion: undefined,
          occupationId: undefined,
          region: undefined,
        });
      }
    };

    // Get user
    getUser();

    // Clean up
    return () => {
      // Clear user
      officeContext.clearUserForm();

      // Log
      console.debug('🧹 User form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Switch>
      <PermissionsRequiredRoute
        exact
        path={`${path}${ScreenPaths.Senior_Profile}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Profile,
            canRead: true, // users must be able to at least read the profile to access the page
          },
        ]}
      >
        <SeniorProfileProfessionalCaregiverOfficeScreen />
      </PermissionsRequiredRoute>

      <PermissionsRequiredRoute
        exact
        path={`${path}${ScreenPaths.Senior_Caregivers}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Caregivers,
            canRead: true, // users must be able to at least read the caregivers to access the page
          },
        ]}
      >
        <SeniorInformalCaregiversProfessionalCaregiverOfficeScreen />
      </PermissionsRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_Applications}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Applications,
            canRead: true, // users must be able to at least read the applications to access the page
          },
        ]}
      >
        <SeniorApplicationsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>
      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_Devices}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Devices,
            canRead: true, // users must be able to at least read the devices to access the page
          },
        ]}
      >
        <SeniorDevicesProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_AssociateDevice}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Devices,
            canWrite: true, // users must be able to write devices to access the page
          },
        ]}
      >
        <SeniorAssociateDeviceProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_Device}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Devices,
            canRead: true, // users must be able to at least read the devices to access the page
          },
        ]}
      >
        <SeniorDeviceDetailsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_UserCards}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Cards,
            canRead: true, // users must be able to at least read cards to access the page
          },
        ]}
      >
        <SeniorUserCardsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_AssociateUserCard}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Cards,
            canWrite: true, // users must be able to write cards to access the page
          },
        ]}
      >
        <SeniorAssociateUserCardProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_UserCard}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Cards,
            canRead: true, // users must be able to at least read cards to access the page
          },
        ]}
      >
        <SeniorUserCardProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <PermissionsRequiredRoute
        exact
        path={`${path}${ScreenPaths.Senior_Health}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Health,
            canRead: true, // users must be able to at least read the health to access the page
          },
        ]}
      >
        <SeniorHealthProfessionalCaregiverOfficeScreen />
      </PermissionsRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_Playlists}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Playlists,
            canRead: true, // users must be able to at least read senior playlists to access the page
          },
        ]}
      >
        <SeniorPlaylistsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_PlaylistItems}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Playlists,
            canRead: true, // users must be able to at least read senior playlists to access the page
          },
        ]}
      >
        <SeniorPlaylistItemsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="digital_inclusion"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_PlaylistItem}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_DigitalInclusion_Playlists,
            canRead: true, // users must be able to at least read senior playlists to access the page
          },
        ]}
      >
        <SeniorPlaylistItemProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      <AccessRequiredRoute
        access="care_management"
        entityId={entityId}
        exact
        path={`${path}${ScreenPaths.Senior_Attachments}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Attachments,
            canRead: true, // users must be able to at least read attachments to access the page
          },
        ]}
      >
        <SeniorAttachmentsProfessionalCaregiverOfficeScreen />
      </AccessRequiredRoute>

      {/* Caregiver relationship invites history */}
      <PermissionsRequiredRoute
        exact
        path={`${path}${ScreenPaths.Senior_InvitesHistory}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Caregivers,
            canRead: true, // users must be able to at least read the caregivers to access the page
          },
        ]}
      >
        <SeniorInvitesHistoryProfessionalCaregiverOfficeScreen />
      </PermissionsRequiredRoute>

      {/* TODO: Remove - Will be a redirect for the new invite flow */}

      <PermissionsRequiredRoute
        exact
        path={`${path}${ScreenPaths.Senior_InviteCaregiver}`}
        requiredPermissions={[
          {
            type: RolePermissionType.Seniors_PersonalData_Caregivers,
            canWrite: true, // users must be able to write caregivers to access the page
          },
        ]}
      >
        <SeniorInviteInformalCaregiverProfessionalCaregiverOfficeScreen />
      </PermissionsRequiredRoute>

      {/** Default route - keep the default route here before system admin routes */}
      <Route path={path}>
        <Redirect to={`${url}${defaultRoute}`} />
      </Route>
    </Switch>
  );
}
